import { useState, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { useTranslations } from 'use-intl';
import { Button, Dialog, DialogFooter } from '@leanin/ui';
import { RouterLinkAs } from '@/lib/router-link';
import { internalPath } from './internal-url';

// The single renderer for user- and member-entered links (design 2026-08-06
// Project B). It splits two cases:
//
//   - Internal Connect destinations (relative paths, same-origin, or a
//     connect.leanin.org host) navigate in the SAME tab via the client-side
//     router — no `target="_blank"`, no interstitial — so members stay in
//     their session instead of accumulating browser tabs.
//   - Everything else (cross-origin http(s), the leanin.org marketing site,
//     …) keeps the raw external anchor and gates navigation behind a confirm
//     dialog. Only http(s) web navigations are gated — mailto:/tel: and other
//     native-handler schemes are not "a site outside Lean In Connect" and
//     pass straight through.
//
// The internal/external split is decided from the href string at render time
// (window origin is consulted only when present), so rendering stays
// render-to-string safe. The confirm dialog mounts only after a gated click,
// keeping plain body rendering free of the translations hook. Auxclick
// (middle-click) on an external link is not interceptable here and falls back
// to the anchor's own noopener/noreferrer attributes.
type ExternalLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  readonly href: string;
};

export function ExternalLink({ href, children, onClick, ...rest }: ExternalLinkProps) {
  const [pendingUrl, setPendingUrl] = useState<URL | null>(null);

  const to = internalPath(href, typeof window !== 'undefined' ? window.location.origin : null);
  if (to !== null) {
    return (
      <RouterLinkAs href={to} className={rest.className} style={rest.style} title={rest.title}>
        {children}
      </RouterLinkAs>
    );
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
    event.preventDefault();
    setPendingUrl(url);
  };

  return (
    <>
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest} onClick={handleClick}>
        {children}
      </a>
      {pendingUrl !== null ? (
        <ExternalLinkDialog url={pendingUrl} onClose={() => setPendingUrl(null)} />
      ) : null}
    </>
  );
}

// Mounted only after a gated click, so the translations hook never runs during
// plain body rendering (keeps render-to-string paths free of IntlProvider).
function ExternalLinkDialog({
  url,
  onClose,
}: {
  readonly url: URL;
  readonly onClose: () => void;
}) {
  const t = useTranslations('Common.externalLink');
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('title')}
      description={t('description', { host: url.host })}
      size="sm"
    >
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            window.open(url.href, '_blank', 'noopener,noreferrer');
            onClose();
          }}
        >
          {t('continue')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
